import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@bb/ui';
import type { AdminApi, CommentThreadView } from '../../api/client.ts';
import { meQuery } from '../../api/queries.ts';
import { formatWhen } from '../segments/SegmentsListPage.tsx';
import { MentionInput } from './MentionInput.tsx';

/** One thread: its comments, a reply with mentions, resolve / reopen, deleting one's own comment. */
export function ThreadView({ environmentId, thread, stepName, canWrite }: { environmentId: string; thread: CommentThreadView; stepName?: string | undefined; canWrite: boolean }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const me = useQuery(meQuery(api));
  const [reply, setReply] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['comments', environmentId, thread.objectType, thread.objectId] });
  const send = useMutation({ mutationFn: () => api.replyThread(environmentId, thread.id, { body: reply.trim(), mentions }), onSuccess: () => (setReply(''), setMentions([]), refresh()) });
  const status = useMutation({ mutationFn: (action: 'resolve' | 'reopen') => api.setThreadStatus(environmentId, thread.id, action), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (commentId: string) => api.deleteComment(environmentId, commentId), onSuccess: refresh });
  const first = thread.comments[0];
  return (
    <article className="thread" aria-label={`Thread by ${thread.createdBy?.email ?? 'someone'}${stepName ? ` on ${stepName}` : ''}`}>
      <header className="hint">
        {stepName ? `On step «${stepName}» · ` : ''}
        {thread.status === 'resolved' ? `Resolved${thread.resolvedBy?.email ? ` by ${thread.resolvedBy.email}` : ''}` : 'Open'}
      </header>
      <ol className="thread__comments">
        {thread.comments.map((c) => (
          <li key={c.id}>
            <strong>{c.author?.email ?? 'someone'}</strong> <span className="hint">{formatWhen(c.createdAt)}{c.editedAt ? ' · edited' : ''}</span>
            <p>{c.deleted ? <em className="hint">Comment deleted</em> : c.body}</p>
            {!c.deleted && c.author?.id === me.data?.employee.id && (
              <Button variant="ghost" aria-label={`Delete comment ${c === first ? 'that started the thread' : `from ${formatWhen(c.createdAt)}`}`} onClick={() => remove.mutate(c.id)}>
                Delete
              </Button>
            )}
          </li>
        ))}
      </ol>
      {canWrite && (
        <>
          <MentionInput
            environmentId={environmentId}
            label="Reply"
            value={reply}
            mentions={mentions}
            onChange={(value, next) => {
              setReply(value);
              setMentions(next);
            }}
          />
          <div className="toolbar">
            <Button disabled={!reply.trim()} loading={send.isPending} onClick={() => send.mutate()}>
              Reply
            </Button>
            <Button variant="ghost" onClick={() => status.mutate(thread.status === 'open' ? 'resolve' : 'reopen')}>
              {thread.status === 'open' ? 'Resolve' : 'Reopen'}
            </Button>
          </div>
        </>
      )}
    </article>
  );
}
